import { WorkflowEvent } from 'shared/types/graphql';
import { TimeIncrement } from '../../types/AutomationDraft';

export const inMinutes = {
  Minutes: 1,
  Hours: 60,
  Days: 1440, // 60 * 24
  Weeks: 10080, // 1440 * 7
  Months: 43200, // 1440 * 30
} as Record<TimeIncrement, number>;

export const convertTime = (value: number, originalUnit: TimeIncrement, newUnit: TimeIncrement): number => {
  const minutes = value * inMinutes[originalUnit];
  return Math.floor(minutes / inMinutes[newUnit]);
};

/**
 * Convert a time in minutes to a reasonable display value with time unit in minutes, hours, days, weeks, or months
 * If *_NOT_* using the lapsed pet parent automation type, only convert value to days if time of day is specified
 * Generic automation type examples:
 * - If input is 120, this returns as "2 hours"
 * - If input is 1440, this returns as "24 hours"
 * - If input 1440 and time of day is specified, this returns as "1 day"
 * Lapsed pet parent automation type examples:
 * - If input is 40320, this returns as "1 month"
 * - If input is 1440, this returns as "1 day"
 */
export const convertMinutesToTimeDisplay = ({
  timeInMinutes,
  automationType,
  isTimeOfDaySpecified = false,
}: {
  timeInMinutes: number;
  automationType: WorkflowEvent;
  isTimeOfDaySpecified: boolean;
}): { timeValue: number; timeIncrement: TimeIncrement } => {
  const timeInHours = timeInMinutes / 60;
  const timeInDays = timeInHours / 24;
  const timeInWeeks = timeInDays / 7;
  const timeInMonths = timeInDays / 30;

  let timeValue = timeInMinutes;
  let timeIncrement = TimeIncrement.Minutes;

  if (automationType === WorkflowEvent.LapsedPetParent) {
    if (timeInMonths % 1 === 0) {
      timeValue = timeInMonths;
      timeIncrement = TimeIncrement.Months;
    } else if (timeInWeeks % 1 === 0) {
      timeValue = timeInWeeks;
      timeIncrement = TimeIncrement.Weeks;
    } else if (timeInDays % 1 === 0) {
      timeValue = timeInDays;
      timeIncrement = TimeIncrement.Days;
    }
  } else {
    if (timeInDays % 1 === 0 && isTimeOfDaySpecified) {
      timeValue = timeInDays;
      timeIncrement = TimeIncrement.Days;
    } else if (timeInHours % 1 === 0) {
      timeValue = timeInHours;
      timeIncrement = TimeIncrement.Hours;
    }
  }

  return {
    timeValue,
    timeIncrement,
  };
};

export const convertToSeconds = ({ amount, increment }: { amount: number; increment: TimeIncrement }): number => {
  let multiplier = 1;

  switch (increment) {
    case TimeIncrement.Minutes:
      multiplier = 60;
      break;
    case TimeIncrement.Hours:
      multiplier = 60 * 60;
      break;
    case TimeIncrement.Days:
      multiplier = 60 * 60 * 24;
      break;
    case TimeIncrement.Weeks:
      multiplier = 60 * 60 * 24 * 7;
      break;
    case TimeIncrement.Months:
      multiplier = 60 * 60 * 24 * 30;
      break;
    default:
      break;
  }

  return amount * multiplier;
};

export enum ExtraTimeIncrement {
  Seconds = 'Seconds',
}

// convert provided value to smallest whole number increment
// default is 1 Hour
export const getSmallestTimeIncrement = (
  amountInSeconds = 3600,
  allowedIncrements: TimeIncrement[] = [TimeIncrement.Minutes, TimeIncrement.Hours, TimeIncrement.Days],
): { amount: number; increment: TimeIncrement | ExtraTimeIncrement } => {
  const conversionToSeconds = {
    [TimeIncrement.Minutes]: 60,
    [TimeIncrement.Hours]: 60 * 60,
    [TimeIncrement.Days]: 60 * 60 * 24,
    [TimeIncrement.Weeks]: 60 * 60 * 24 * 7,
    [TimeIncrement.Months]: 60 * 60 * 24 * 30,
  };

  const isAmountDivisible = (increment: TimeIncrement): boolean => {
    return amountInSeconds % conversionToSeconds[increment] === 0;
  };

  let amount = amountInSeconds;
  let increment: TimeIncrement | ExtraTimeIncrement = ExtraTimeIncrement.Seconds;

  const sortedIncrements = allowedIncrements.sort((a, b) => (conversionToSeconds[a] > conversionToSeconds[b] ? -1 : 1));

  for (const timeIncrement of sortedIncrements) {
    if (sortedIncrements.includes(timeIncrement) && isAmountDivisible(timeIncrement)) {
      amount = amountInSeconds / conversionToSeconds[timeIncrement];
      increment = timeIncrement;

      return { amount, increment };
    }
  }

  return { amount, increment };
};
